/**
 * Cockpit visual editor — the schema-driven PROPS FORM.
 *
 * Renders an editable form for ONE selected node's props, with fields derived
 * from that component's Zod `propsSchema` (via `deriveFields`, which walks the
 * JSON-Schema serialization). Supports:
 *
 *   - scalar fields: string, number, integer, boolean (switch), enum (select),
 *     string-or-number union,
 *   - binding / action slot props → a select of the declared ids (+ an "(none)"
 *     option for optional slots), so refs always validate,
 *   - nested object groups (e.g. MetricCard.delta) as a labelled fieldset,
 *   - arrays of scalars OR objects (e.g. DataTable.columns, ButtonGroup.buttons)
 *     with add/remove rows,
 *   - tuples (e.g. MapView.bbox) as fixed-length number rows.
 *
 * Every edit produces a NEW props bag handed up via `onChange`; the parent
 * re-validates the whole spec with `parseCockpitSpec` and surfaces structured
 * errors inline (component-not-in-catalog, prop violation, undeclared ref).
 */

import { useId } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  blankValueForField,
  type FieldDescriptor,
} from './cockpit-editor-model';

const NONE_VALUE = '__none__';

export interface CockpitPropsFormProps {
  /** The top-level field descriptors for the selected component. */
  fields: FieldDescriptor[];
  /** The current props bag. */
  props: Record<string, unknown>;
  /** Declared binding ids (for binding-slot pickers). */
  bindingIds: string[];
  /** Declared action ids (for action-slot pickers). */
  actionIds: string[];
  /** Called with the next props bag on any edit. */
  onChange: (next: Record<string, unknown>) => void;
  /** Per-prop error messages keyed by the prop path under `props.`. */
  errorsByPath?: Record<string, string>;
}

export function CockpitPropsForm({
  fields,
  props,
  bindingIds,
  actionIds,
  onChange,
  errorsByPath = {},
}: CockpitPropsFormProps) {
  const setKey = (key: string, value: unknown) => {
    const next = { ...props };
    if (value === undefined) delete next[key];
    else next[key] = value;
    onChange(next);
  };

  if (fields.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        This component has no editable props.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {fields.map((field) => (
        <FieldRow
          key={field.key}
          field={field}
          value={props[field.key]}
          bindingIds={bindingIds}
          actionIds={actionIds}
          onChange={(v) => setKey(field.key, v)}
          path={field.key}
          errorsByPath={errorsByPath}
        />
      ))}
    </div>
  );
}

// ─── One field row (recursive) ───────────────────────────────────────────────

interface FieldRowProps {
  field: FieldDescriptor;
  value: unknown;
  bindingIds: string[];
  actionIds: string[];
  onChange: (next: unknown) => void;
  path: string;
  errorsByPath: Record<string, string>;
}

function FieldRow({
  field,
  value,
  bindingIds,
  actionIds,
  onChange,
  path,
  errorsByPath,
}: FieldRowProps) {
  const id = useId();
  const error = errorsByPath[path];

  // ── binding / action slot → declared-id picker ──
  if (field.kind === 'binding' || field.kind === 'action') {
    const options = field.kind === 'binding' ? bindingIds : actionIds;
    const current = typeof value === 'string' && value.length > 0 ? value : NONE_VALUE;
    return (
      <FieldShell label={field.label} required={field.required} htmlFor={id} error={error}
        hint={field.kind === 'binding' ? 'Data binding' : 'Action'}>
        <Select
          value={options.includes(current) || current === NONE_VALUE ? current : current}
          onValueChange={(v) => onChange(v === NONE_VALUE ? undefined : v)}
        >
          <SelectTrigger id={id} className="h-8 text-xs">
            <SelectValue placeholder={`Pick a ${field.kind}…`} />
          </SelectTrigger>
          <SelectContent>
            {!field.required && (
              <SelectItem value={NONE_VALUE} className="text-xs italic text-muted-foreground">
                (none)
              </SelectItem>
            )}
            {options.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                No {field.kind}s declared yet — add one in the Bindings tab.
              </div>
            ) : (
              options.map((opt) => (
                <SelectItem key={opt} value={opt} className="text-xs font-mono">
                  {opt}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </FieldShell>
    );
  }

  switch (field.kind) {
    case 'string':
      return (
        <FieldShell label={field.label} required={field.required} htmlFor={id} error={error}
          hint={field.maxLength ? `≤ ${field.maxLength} chars` : undefined}>
          <Input
            id={id}
            value={typeof value === 'string' ? value : ''}
            maxLength={field.maxLength}
            onChange={(e) => onChange(e.target.value === '' && !field.required ? undefined : e.target.value)}
            className="h-8 text-xs"
          />
        </FieldShell>
      );

    case 'number':
    case 'integer':
      return (
        <FieldShell label={field.label} required={field.required} htmlFor={id} error={error}
          hint={numberHint(field)}>
          <Input
            id={id}
            type="number"
            value={typeof value === 'number' ? value : ''}
            min={field.minimum}
            max={field.maximum}
            step={field.kind === 'integer' ? 1 : 'any'}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') return onChange(undefined);
              const n = field.kind === 'integer' ? parseInt(raw, 10) : Number(raw);
              onChange(Number.isFinite(n) ? n : undefined);
            }}
            className="h-8 text-xs"
          />
        </FieldShell>
      );

    case 'string-or-number':
      // Free text; coerce to number when it parses cleanly so the union accepts it.
      return (
        <FieldShell label={field.label} required={field.required} htmlFor={id} error={error}
          hint="text or number">
          <Input
            id={id}
            value={value === undefined || value === null ? '' : String(value)}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '' && !field.required) return onChange(undefined);
              const asNum = Number(raw);
              onChange(raw.trim() !== '' && Number.isFinite(asNum) && /^-?\d*\.?\d+$/.test(raw.trim()) ? asNum : raw);
            }}
            className="h-8 text-xs"
          />
        </FieldShell>
      );

    case 'boolean':
      return (
        <div className="flex items-center justify-between gap-2 py-0.5">
          <Label htmlFor={id} className="text-xs font-medium">
            {field.label}
            {field.required ? <span className="text-destructive"> *</span> : null}
          </Label>
          <Switch
            id={id}
            checked={value === true}
            onCheckedChange={(checked) => onChange(checked)}
          />
        </div>
      );

    case 'enum':
      return (
        <FieldShell label={field.label} required={field.required} htmlFor={id} error={error}>
          <Select
            value={typeof value === 'string' ? value : (field.required ? (field.enumValues?.[0] ?? '') : NONE_VALUE)}
            onValueChange={(v) => onChange(v === NONE_VALUE ? undefined : v)}
          >
            <SelectTrigger id={id} className="h-8 text-xs">
              <SelectValue placeholder="Choose…" />
            </SelectTrigger>
            <SelectContent>
              {!field.required && (
                <SelectItem value={NONE_VALUE} className="text-xs italic text-muted-foreground">
                  (default)
                </SelectItem>
              )}
              {(field.enumValues ?? []).map((opt) => (
                <SelectItem key={opt} value={opt} className="text-xs">
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldShell>
      );

    case 'object': {
      const obj = (value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {}) as Record<string, unknown>;
      const present = value !== undefined && value !== null;
      return (
        <div className="rounded-md border border-border/60 p-2.5">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-semibold">
              {field.label}
              {field.required ? <span className="text-destructive"> *</span> : null}
            </span>
            {!field.required && (
              <Switch
                checked={present}
                onCheckedChange={(checked) =>
                  onChange(checked ? blankValueForField(field) : undefined)
                }
                aria-label={`Toggle ${field.label}`}
              />
            )}
          </div>
          {error ? <p className="mb-1.5 text-[11px] text-destructive">{error}</p> : null}
          {present ? (
            <div className="flex flex-col gap-2.5 pl-1">
              {(field.fields ?? []).map((child) => (
                <FieldRow
                  key={child.key}
                  field={child}
                  value={obj[child.key]}
                  bindingIds={bindingIds}
                  actionIds={actionIds}
                  onChange={(v) => {
                    const next = { ...obj };
                    if (v === undefined) delete next[child.key];
                    else next[child.key] = v;
                    onChange(next);
                  }}
                  path={`${path}.${child.key}`}
                  errorsByPath={errorsByPath}
                />
              ))}
            </div>
          ) : null}
        </div>
      );
    }

    case 'array': {
      const arr = Array.isArray(value) ? value : [];
      const item = field.item!;
      const atMax = field.maxItems !== undefined && arr.length >= field.maxItems;
      return (
        <div className="rounded-md border border-border/60 p-2.5">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-semibold">
              {field.label}
              {field.required ? <span className="text-destructive"> *</span> : null}
              <span className="ml-1.5 font-normal text-muted-foreground">({arr.length})</span>
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-[11px]"
                  disabled={atMax}
                  onClick={() => onChange([...arr, blankValueForField(item)])}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Add
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {atMax ? `Maximum ${field.maxItems} items` : `Add a ${field.label} item`}
              </TooltipContent>
            </Tooltip>
          </div>
          {error ? <p className="mb-1.5 text-[11px] text-destructive">{error}</p> : null}
          {arr.length === 0 ? (
            <p className="text-[11px] italic text-muted-foreground">No items yet.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {arr.map((entry, i) => (
                <div key={i} className="rounded border border-border/40 bg-muted/20 p-2">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {field.label} {i + 1}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 text-muted-foreground hover:text-destructive"
                          onClick={() => onChange(arr.filter((_, j) => j !== i))}
                          aria-label={`Remove item ${i + 1}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Remove this item</TooltipContent>
                    </Tooltip>
                  </div>
                  {item.kind === 'object' ? (
                    <div className="flex flex-col gap-2 pl-0.5">
                      {(item.fields ?? []).map((child) => {
                        const obj = (entry && typeof entry === 'object' && !Array.isArray(entry)
                          ? (entry as Record<string, unknown>)
                          : {});
                        return (
                          <FieldRow
                            key={child.key}
                            field={child}
                            value={obj[child.key]}
                            bindingIds={bindingIds}
                            actionIds={actionIds}
                            onChange={(v) => {
                              const next = [...arr];
                              const nextObj = { ...obj };
                              if (v === undefined) delete nextObj[child.key];
                              else nextObj[child.key] = v;
                              next[i] = nextObj;
                              onChange(next);
                            }}
                            path={`${path}.${i}.${child.key}`}
                            errorsByPath={errorsByPath}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <FieldRow
                      field={{ ...item, label: `Value`, required: true }}
                      value={entry}
                      bindingIds={bindingIds}
                      actionIds={actionIds}
                      onChange={(v) => {
                        const next = [...arr];
                        next[i] = v;
                        onChange(next);
                      }}
                      path={`${path}.${i}`}
                      errorsByPath={errorsByPath}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    case 'tuple': {
      const arr = Array.isArray(value) ? value : [];
      const positions = field.prefixItems ?? [];
      return (
        <FieldShell label={field.label} required={field.required} htmlFor={id} error={error}>
          <div className="flex flex-wrap gap-1.5">
            {positions.map((pos, i) => (
              <Input
                key={i}
                type={pos.kind === 'number' || pos.kind === 'integer' ? 'number' : 'text'}
                value={arr[i] === undefined ? '' : String(arr[i])}
                placeholder={pos.label}
                onChange={(e) => {
                  const next = [...arr];
                  const raw = e.target.value;
                  next[i] = pos.kind === 'number' || pos.kind === 'integer' ? Number(raw) : raw;
                  onChange(next);
                }}
                className="h-8 w-20 text-xs"
              />
            ))}
          </div>
        </FieldShell>
      );
    }

    default:
      return (
        <FieldShell label={field.label} required={field.required} htmlFor={id} error={error}
          hint="advanced — edit raw JSON">
          <Input
            id={id}
            value={value === undefined ? '' : JSON.stringify(value)}
            onChange={(e) => {
              try {
                onChange(e.target.value === '' ? undefined : JSON.parse(e.target.value));
              } catch {
                /* keep last valid value; surfaced via spec validation */
              }
            }}
            className="h-8 font-mono text-[11px]"
          />
        </FieldShell>
      );
  }
}

// ─── Shared field shell (label + hint + error) ──────────────────────────────

function FieldShell({
  label,
  required,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  required: boolean;
  htmlFor: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={htmlFor} className="text-xs font-medium">
          {label}
          {required ? <span className="text-destructive"> *</span> : null}
        </Label>
        {hint ? <span className="text-[10px] text-muted-foreground">{hint}</span> : null}
      </div>
      {children}
      {error ? <p className="text-[11px] text-destructive">{error}</p> : null}
    </div>
  );
}

function numberHint(field: FieldDescriptor): string | undefined {
  const parts: string[] = [];
  if (field.minimum !== undefined) parts.push(`≥ ${field.minimum}`);
  if (field.maximum !== undefined && field.maximum < 1e15) parts.push(`≤ ${field.maximum}`);
  return parts.length > 0 ? parts.join(', ') : undefined;
}
